/**
 * CashSpend — the one "log a cash spend" flow, surfaced anywhere cash is spent
 * (dashboard toolbar, map place, +New) rather than only under Manage → Cash.
 * Cash is a SOURCE like a bank: the spend comes OUT of a money spot, drops cash
 * on hand, and (optionally) counts to a linked expense and a shop/place — a
 * reversible movement, never a silent commit. The amount lands in an editable
 * field with a plain-English read-back. The shop is picked with the shared
 * ShopField (search saved · 📍 current location · type an address → map pin ·
 * 🎤 voice) and the expense is a name-searchable + voice picker, not a bare
 * dropdown. Posts to /budget/money-spots/{id}/spend/.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.js';
import { fmtR } from '../money.js';
import ShopField from './ShopField.jsx';
import VoiceButton from './VoiceButton.jsx';
import SearchField from './SearchField.jsx';

export default function CashSpend({ items = [], place = null, onClose, onDone }) {
  const [spots, setSpots] = useState(null);
  const [spotId, setSpotId] = useState('');
  const [amount, setAmount] = useState('');
  const [itemId, setItemId] = useState('');
  const [expQuery, setExpQuery] = useState('');   // searchable expense picker
  const [expOpen, setExpOpen] = useState(false);
  const [shop, setShop] = useState(null);          // ShopField selection (place==null)
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api('/budget/money-spots/').then((d) => {
      const list = (Array.isArray(d) ? d : (d?.results || [])).filter((s) => !s.archived);
      setSpots(list);
      if (list.length >= 1) setSpotId(String(list[0].id));
    }).catch(() => setSpots([]));
  }, []);

  const expenses = (items || []).filter((i) => i.kind === 'expense');
  const amt = Number(String(amount).replace(',', '.')) || 0;
  const spot = (spots || []).find((s) => String(s.id) === String(spotId));
  const onHand = spot ? Number(spot.balance || 0) : null;
  const pickedExpense = expenses.find((i) => String(i.id) === String(itemId));
  const placeName = place != null ? null : (shop?.name || '');

  const expFiltered = useMemo(() => {
    const q = expQuery.trim().toLowerCase();
    return expenses
      .filter((i) => !q || (i.title || '').toLowerCase().includes(q))
      .slice(0, q ? 40 : 8);
  }, [expenses, expQuery]);

  // Plain-English read-back — editable field, never a silent commit.
  const readback = amt > 0
    ? `${fmtR(amt)} cash out of ${spot ? spot.name : 'your spot'}${placeName ? ` at ${placeName}` : ''}`
      + `${itemId ? ' — counts to the linked expense' : ''}. Cash on hand drops by ${fmtR(amt)}.`
      + (onHand != null ? ` ${fmtR(Math.max(0, onHand - amt))} left in ${spot.name}.` : '')
    : 'Type an amount and Vault records a cash spend out of your spot — cash on hand drops, reversible.';

  const submit = async () => {
    if (!spotId) { setErr('Pick a cash spot.'); return; }
    if (!(amt > 0)) { setErr('Enter an amount.'); return; }
    setBusy(true); setErr('');
    try {
      // Resolve the shop to a place id: locked place from a map pin, an existing
      // saved place, or a freshly geocoded/pinned one we create now.
      let placeArg = place != null ? place : null;
      if (place == null && shop) {
        if (shop.placeId) placeArg = shop.placeId;
        else if (shop.newPlace && shop.newPlace.lat != null) {
          try {
            const np = await api('/budget/places/', {
              method: 'POST',
              body: {
                name: (shop.newPlace.name || 'Pinned location').slice(0, 60),
                address: shop.newPlace.address || '',
                lat: shop.newPlace.lat, lng: shop.newPlace.lng,
                kind: shop.newPlace.source === 'address' ? 'address' : 'shop',
              },
            });
            placeArg = np.id;
          } catch { placeArg = null; }
        }
      }
      await api(`/budget/money-spots/${spotId}/spend/`, {
        method: 'POST',
        body: { amount: amt, item: itemId || null, place: placeArg, note: note.trim() },
      });
      onDone?.();
      onClose?.();
    } catch (e) { setErr(e.data?.detail || 'Could not record the spend.'); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-end md:place-items-center z-50" onClick={onClose}>
      <div data-guide="cash-form" className="bg-panel border border-edge rounded-t-2xl md:rounded-2xl w-full md:max-w-md max-h-[92vh] overflow-y-auto p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <h2 className="font-bold text-lg">💵 Log a cash spend</h2>
          {onHand != null && <span className="ml-auto text-xs text-accent-300 text-slate-400">{fmtR(onHand)} on hand</span>}
          <button className="text-slate-500 hover:text-white ml-2" onClick={onClose}>✕</button>
        </div>
        {spots === null ? (
          <p className="text-sm text-slate-500 mt-4">Loading your cash spots…</p>
        ) : spots.length === 0 ? (
          <p className="text-sm text-slate-400 mt-4">No money spots yet — add one under Manage → Cash first.</p>
        ) : (
          <div className="mt-4 grid gap-3">
            <div className="flex items-center gap-2">
              <input className="input text-lg flex-1" type="text" inputMode="decimal" placeholder="R amount"
                     value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
              <VoiceButton onText={(t) => {
                const m = String(t).replace(/[rR]/g, '').match(/\d[\d\s.,]*/);
                if (m) setAmount(m[0].replace(/\s/g, ''));
              }} title="Say the amount" />
            </div>
            {spots.length > 1 && (
              <label className="text-sm text-slate-400">Out of
                <select className="input mt-1" value={spotId} onChange={(e) => setSpotId(e.target.value)}>
                  {spots.map((s) => <option key={s.id} value={s.id}>💵 {s.name} · {fmtR(s.balance)}</option>)}
                </select>
              </label>
            )}
            {place == null && (
              <div className="text-sm text-slate-400">Shop / location
                <div className="mt-1"><ShopField value={shop} onChange={setShop} expenseTitle={pickedExpense?.title || ''} /></div>
              </div>
            )}
            <div className="text-sm text-slate-400">On (optional)
              {pickedExpense && !expOpen ? (
                <div className="mt-1 flex items-center gap-2">
                  <span className="flex-1 input flex items-center truncate">🧾 {pickedExpense.title}</span>
                  <button type="button" className="text-xs text-vblue hover:underline" onClick={() => { setExpOpen(true); setExpQuery(''); }}>Change</button>
                  <button type="button" className="text-xs text-slate-500 hover:text-slate-300" onClick={() => setItemId('')}>Clear</button>
                </div>
              ) : (
                <div className="mt-1 grid gap-1.5">
                  <SearchField value={expQuery} placeholder="Search an expense by name (optional)"
                               parse={false}
                               onChange={(t) => { setExpQuery(t); setExpOpen(true); }}
                               onVoice={(t) => { setExpQuery(t); setExpOpen(true); }} />
                  {expOpen && (
                    <div className="grid gap-1 max-h-44 overflow-y-auto">
                      {expFiltered.map((i) => (
                        <button key={i.id} type="button"
                                className="text-left border border-edge rounded-lg px-2.5 py-1.5 text-sm hover:border-vblue w-full min-w-0 block truncate"
                                onClick={() => { setItemId(String(i.id)); setExpOpen(false); }}>
                          🧾 {i.title}
                        </button>
                      ))}
                      {expFiltered.length === 0 && <p className="text-xs text-slate-500 px-1">No matching expense.</p>}
                      <button type="button" className="text-[11px] text-slate-600 text-left" onClick={() => { setExpOpen(false); setItemId(''); }}>
                        skip — no expense
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <input className="input" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="text-[12.5px] text-vblue border border-vblue/30 rounded-lg px-2.5 py-1.5 leading-relaxed">✨ {readback}</div>
            {err && <p className="text-xs text-red-400">{err}</p>}
            <button className="btn-primary disabled:opacity-40" disabled={busy || !(amt > 0)} onClick={submit}>
              {busy ? 'Recording…' : (amt > 0 ? `✓ Log cash spend ${fmtR(amt)}` : 'Enter an amount')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
